import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, FormControl, InputLabel,
  Select, MenuItem, Button, Chip, IconButton, Alert, CircularProgress,
  Checkbox, ListItemText, OutlinedInput, Stack, Avatar, Divider,
  TextField, InputAdornment, Tooltip,
} from '@mui/material';
import {
  Add, Delete, LocalHospital, SelectAll, ClearAll,
  PersonAdd, Search as SearchIcon, Group as GroupIcon,
  Apartment as WardIcon, AssignmentInd as AssignedIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  LoadUsersdb,
  loadWardsdb,
  loadNurseWardAssignmentsdb,
  assignNurseToWarddb,
  removeNurseWardAssignmentdb,
} from '../BaseURL';

const PRIMARY = '#1A9B7F';

// Soft pastel palette for ward header strips, picked deterministically by ward id
const WARD_PALETTE = [
  { tint: '#0EA5E9', bg: '#E0F2FE' },
  { tint: '#8B5CF6', bg: '#EDE9FE' },
  { tint: '#EC4899', bg: '#FCE7F3' },
  { tint: '#F59E0B', bg: '#FEF3C7' },
  { tint: '#10B981', bg: '#D1FAE5' },
  { tint: '#EF4444', bg: '#FEE2E2' },
  { tint: '#6366F1', bg: '#E0E7FF' },
  { tint: '#14B8A6', bg: '#CCFBF1' },
];

const colorForWard = (id) => WARD_PALETTE[Number(id) % WARD_PALETTE.length];

const initialsOf = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';

export default function NurseWardAssignment() {
  const [users,       setUsers]       = useState([]);
  const [wards,       setWards]       = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);

  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedWardIds, setSelectedWardIds] = useState([]);
  const [wardSearch, setWardSearch] = useState('');

  const allWardsSelected = wards.length > 0 && selectedWardIds.length === wards.length;
  const toggleAllWards = () => {
    setSelectedWardIds(allWardsSelected ? [] : wards.map(w => w.id));
  };

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [usersRes, wardsRes, assignRes] = await Promise.all([
        // Only staff whose role has been granted access to dept 5 (Wards & Nursing Services)
        axios.get(LoadUsersdb, { params: { accessDepartmentId: 5 } }),
        axios.get(loadWardsdb),
        axios.get(loadNurseWardAssignmentsdb),
      ]);

      setUsers(usersRes.data.results || []);
      setWards(wardsRes.data.wards || []);
      setAssignments(assignRes.data.results || []);

    } catch (err) {
      Swal.fire('Error', 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedStaff || selectedWardIds.length === 0) {
      Swal.fire('Warning', 'Please select a staff member and at least one ward', 'warning');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(assignNurseToWarddb, {
        staffCode:  selectedStaff,
        wardIds:    selectedWardIds,
        assignedBy: userInfo.name,
      });
      if (res.data.success) {
        Swal.fire('Done', res.data.message || 'Ward assignment saved', 'success');
        setSelectedStaff('');
        setSelectedWardIds([]);
        loadAll();
      } else {
        Swal.fire('Error', res.data.message, 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to assign', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id, nurseName, wardName) => {
    const ok = await Swal.fire({
      title: 'Remove Assignment?',
      html: `Remove <b>${nurseName}</b> from <b>${wardName}</b>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Remove',
    });
    if (!ok.isConfirmed) return;

    try {
      await axios.post(removeNurseWardAssignmentdb, { id });
      loadAll();
    } catch {
      Swal.fire('Error', 'Failed to remove assignment', 'error');
    }
  };

  // Group assignments by ward
  const byWard = useMemo(() => wards.map(w => ({
    ...w,
    nurses: assignments.filter(a => a.ward_id === w.id),
  })), [wards, assignments]);

  // Filter ward cards by search
  const filteredWards = useMemo(() => {
    if (!wardSearch.trim()) return byWard;
    const q = wardSearch.toLowerCase();
    return byWard.filter(w =>
      w.name.toLowerCase().includes(q)
      || w.nurses.some(n => (n.nurse_name || '').toLowerCase().includes(q))
    );
  }, [byWard, wardSearch]);

  // Top stats
  const totalAssignments = assignments.length;
  const wardsWithStaff = byWard.filter(w => w.nurses.length > 0).length;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: PRIMARY }} />
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, fontFamily: '"Inter","Segoe UI",Arial,sans-serif' }}>

      {/* ── Page header ───────────────────────────────────────── */}
      <Box sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.75,
            bgcolor: `${PRIMARY}14`, color: PRIMARY,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AssignedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              Ward Assignments
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
              Assign nurses & midwives to one ward, several wards, or all wards.
            </Typography>
          </Box>
        </Stack>

        {/* Stats row */}
        <Stack direction="row" spacing={1.25} sx={{ mt: 2 }} flexWrap="wrap">
          {[
            { icon: <GroupIcon sx={{ fontSize: 16 }} />, label: 'Nursing staff',  value: users.length,        color: PRIMARY },
            { icon: <WardIcon sx={{ fontSize: 16 }} />,  label: 'Wards',          value: wards.length,        color: '#2563eb' },
            { icon: <AssignedIcon sx={{ fontSize: 16 }} />, label: 'Assignments', value: totalAssignments,    color: '#7c3aed' },
            { icon: <LocalHospital sx={{ fontSize: 16 }} />, label: 'Staffed wards', value: `${wardsWithStaff}/${wards.length}`, color: '#0EA5E9' },
          ].map(s => (
            <Chip
              key={s.label}
              icon={React.cloneElement(s.icon, { sx: { color: `${s.color} !important`, fontSize: 16 } })}
              label={<><strong>{s.value}</strong> {s.label}</>}
              variant="outlined"
              sx={{
                borderColor: `${s.color}33`,
                bgcolor: `${s.color}0D`,
                color: '#334155',
                fontWeight: 500,
                '& .MuiChip-label': { px: 1 },
                height: 30,
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* ── Add Assignment Form ───────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #e5e9ef',
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{
          px: 2.5, py: 1.5,
          bgcolor: `${PRIMARY}08`,
          borderBottom: '1px solid #e5e9ef',
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <PersonAdd sx={{ fontSize: 18, color: PRIMARY }} />
          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
            Add New Assignment
          </Typography>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
          >
            {/* ── Nurse selector ─────────────────────────── */}
            <FormControl size="small" sx={{ flex: 1, minWidth: { md: 240 } }}>
              <InputLabel shrink>Select Nursing Staff</InputLabel>
              <Select
                value={selectedStaff}
                label="Select Nursing Staff"
                onChange={e => setSelectedStaff(e.target.value)}
                displayEmpty
                notched
                MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                renderValue={(val) => {
                  if (!val) return <span style={{ color: '#94a3b8' }}>Choose a nurse / midwife…</span>;
                  const u = users.find(u => u.trans_code === val);
                  return u ? `${u.name} — ${u.role}` : '';
                }}
                sx={{ bgcolor: '#fff' }}
              >
                {users.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText
                      primary="No nursing staff found"
                      secondary="Make sure their role is mapped to the Wards & Nursing Services department."
                    />
                  </MenuItem>
                ) : users.map(u => (
                  <MenuItem key={u.trans_code} value={u.trans_code} dense>
                    <Avatar sx={{ width: 26, height: 26, fontSize: 12, bgcolor: PRIMARY, mr: 1.25 }}>
                      {initialsOf(u.name)}
                    </Avatar>
                    <ListItemText
                      primary={u.name}
                      secondary={u.role}
                      primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ── Ward multi-select ──────────────────────── */}
            <FormControl size="small" sx={{ flex: 1, minWidth: { md: 240 } }}>
              <InputLabel shrink>Select Ward(s)</InputLabel>
              <Select
                multiple
                value={selectedWardIds}
                onChange={e => setSelectedWardIds(
                  typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                )}
                input={<OutlinedInput label="Select Ward(s)" notched />}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) return <span style={{ color: '#94a3b8' }}>Choose ward(s)…</span>;
                  if (selected.length === wards.length) return 'All Wards';
                  if (selected.length === 1) {
                    return wards.find(w => w.id === selected[0])?.name || '';
                  }
                  return `${selected.length} wards selected`;
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                sx={{ bgcolor: '#fff' }}
              >
                {wards.map(w => {
                  const c = colorForWard(w.id);
                  return (
                    <MenuItem key={w.id} value={w.id} dense>
                      <Checkbox size="small" checked={selectedWardIds.includes(w.id)} />
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        bgcolor: c.tint, mr: 1, flexShrink: 0,
                      }} />
                      <ListItemText primary={w.name} primaryTypographyProps={{ fontSize: '0.88rem' }} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* ── All Wards toggle ───────────────────────── */}
            <Tooltip title={allWardsSelected ? 'Clear all wards' : 'Select all wards'} arrow>
              <span>
                <Button
                  variant={allWardsSelected ? 'contained' : 'outlined'}
                  size="small"
                  onClick={toggleAllWards}
                  disabled={wards.length === 0}
                  startIcon={allWardsSelected ? <ClearAll /> : <SelectAll />}
                  sx={{
                    whiteSpace: 'nowrap', textTransform: 'none', flexShrink: 0,
                    height: 40, px: 2,
                    ...(allWardsSelected
                      ? { bgcolor: PRIMARY, '&:hover': { bgcolor: '#157763' } }
                      : { borderColor: '#cbd5e1', color: '#475569', '&:hover': { borderColor: PRIMARY, color: PRIMARY, bgcolor: `${PRIMARY}08` } }),
                  }}
                >
                  {allWardsSelected ? 'Clear' : 'All Wards'}
                </Button>
              </span>
            </Tooltip>

            {/* ── Assign button ──────────────────────────── */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAssign}
              disabled={saving || !selectedStaff || selectedWardIds.length === 0}
              sx={{
                bgcolor: PRIMARY,
                textTransform: 'none', fontWeight: 600,
                height: 40, minWidth: { xs: '100%', md: 140 },
                flexShrink: 0,
                '&:hover': { bgcolor: '#157763' },
                '&:disabled': { bgcolor: '#e5e9ef', color: '#94a3b8' },
              }}
            >
              {saving ? 'Saving…' : `Assign${selectedWardIds.length > 1 ? ` (${selectedWardIds.length})` : ''}`}
            </Button>
          </Stack>

          {/* Selected wards preview as removable chips */}
          {selectedWardIds.length > 0 && (
            <Box sx={{ mt: 1.75, pt: 1.5, borderTop: '1px dashed #e5e9ef' }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {selectedWardIds.map(id => {
                  const w = wards.find(x => x.id === id);
                  if (!w) return null;
                  const c = colorForWard(id);
                  return (
                    <Chip
                      key={id}
                      size="small"
                      label={w.name}
                      onDelete={() => setSelectedWardIds(selectedWardIds.filter(x => x !== id))}
                      sx={{
                        bgcolor: c.bg, color: c.tint, fontWeight: 600,
                        '& .MuiChip-deleteIcon': { color: c.tint },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
        </Box>
      </Paper>

      {/* ── Search bar above ward grid ────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.75 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', flex: 1 }}>
          Wards &amp; Their Assigned Staff
        </Typography>
        <TextField
          size="small"
          placeholder="Filter by ward or nurse name…"
          value={wardSearch}
          onChange={e => setWardSearch(e.target.value)}
          sx={{
            width: { xs: '100%', sm: 280 },
            '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' },
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
          }}
        />
      </Stack>

      {/* ── Wards grid ────────────────────────────────────────── */}
      {filteredWards.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 5, textAlign: 'center',
          border: '1px dashed #cbd5e1', borderRadius: 2.5, bgcolor: '#fff',
        }}>
          <WardIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            {wardSearch ? 'No wards match your search.' : 'No wards have been created yet.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredWards.map(ward => {
            const c = colorForWard(ward.id);
            return (
              <Grid item xs={12} sm={6} lg={4} key={ward.id}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid #e5e9ef',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    height: '100%',
                    display: 'flex', flexDirection: 'column',
                    transition: 'box-shadow .18s ease, transform .18s ease',
                    '&:hover': { boxShadow: '0 6px 18px rgba(15,23,42,0.06)' },
                  }}
                >
                  {/* Ward header strip */}
                  <Box sx={{
                    px: 2, py: 1.25,
                    bgcolor: c.bg,
                    borderBottom: `1px solid ${c.tint}33`,
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}>
                    <Box sx={{
                      width: 30, height: 30, borderRadius: 1.25,
                      bgcolor: '#fff', color: c.tint,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <LocalHospital sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{
                      fontWeight: 700, color: '#0f172a',
                      fontSize: '0.92rem', lineHeight: 1.2,
                      flex: 1, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {ward.name}
                    </Typography>
                    <Chip
                      label={ward.nurses.length}
                      size="small"
                      sx={{
                        bgcolor: '#fff', color: c.tint, fontWeight: 700,
                        height: 22, minWidth: 28,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </Box>

                  {/* Body — list of assigned nurses or empty state */}
                  {ward.nurses.length === 0 ? (
                    <Box sx={{
                      flex: 1, p: 2,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      textAlign: 'center', gap: 0.5,
                      color: '#94a3b8',
                    }}>
                      <PersonAdd sx={{ fontSize: 26, opacity: 0.5 }} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b' }}>
                        No staff assigned
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', maxWidth: 220 }}>
                        Visible to nurses with no assignments of their own.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, maxHeight: 280, overflowY: 'auto', p: 0.5 }}>
                      {ward.nurses.map((n, idx) => (
                        <Box
                          key={n.id}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1.25,
                            px: 1.5, py: 1,
                            borderRadius: 1.5,
                            transition: 'background-color .15s ease',
                            '&:hover': { bgcolor: '#f8fafc' },
                            '&:hover .remove-btn': { opacity: 1 },
                            ...(idx > 0 && { borderTop: '1px solid #f1f5f9' }),
                          }}
                        >
                          <Avatar sx={{
                            width: 32, height: 32, fontSize: 12, fontWeight: 700,
                            bgcolor: c.tint, color: '#fff',
                            flexShrink: 0,
                          }}>
                            {initialsOf(n.nurse_name)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{
                              fontSize: '0.85rem', fontWeight: 600, color: '#0f172a',
                              lineHeight: 1.25,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {n.nurse_name}
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.72rem', color: '#64748b',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {n.nurse_role || 'Nursing staff'}
                              {n.assigned_date && ` · ${n.assigned_date}`}
                            </Typography>
                          </Box>
                          <Tooltip title="Remove from ward" arrow>
                            <IconButton
                              size="small"
                              className="remove-btn"
                              onClick={() => handleRemove(n.id, n.nurse_name, ward.name)}
                              sx={{
                                color: '#ef4444',
                                opacity: { xs: 1, md: 0.4 },
                                transition: 'opacity .15s ease',
                                '&:hover': { bgcolor: '#fee2e2' },
                              }}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
